import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Note } from '../../../shared/interface/note';
import { NoteService } from '../../../shared/services/note.service';
import { ConfirmDialogComponent } from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { UserPermissions } from '../../../shared/interface/user-permissions';
import { AuthService } from '../../../shared/services/auth.service';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { HttpErrorResponse } from "@angular/common/http";
import { ActivatedRoute, Router } from '@angular/router';
import { NgForm } from "@angular/forms";
import { delay, distinctUntilChanged, takeUntil, tap } from "rxjs/operators";
import { select, Store } from "@ngrx/store";
import { getCurrentRouteState } from "../../../shared/ngrx/selector";
import { RouterStateUrl } from "../../../shared/ngrx/route-serializer";
import { FilterService } from "../../../shared/services/filter.service";
import { StoreRootState } from "../../../shared/ngrx/reducers";
import { Partner } from "../../../shared/interface/partner";
import { Ofwca } from "../../../shared/interface/ofwca";
import { CountedAndPaginated } from '../../../shared/interface/response/counted-and-paginated';
import { NoteAddDialogComponent } from "../note-add-dialog/note-add-dialog.component";
import { Uuid } from "../../../shared/interface/response/uuid";
import { JobResultDialogComponent } from "../../../shared/components/job-result-dialog/job-result-dialog.component";
import { MatSort, Sort } from '@angular/material/sort';
import {Profile} from "../../../shared/interface/profile";

@Component({
  selector: 'app-all-note-list',
  templateUrl: './all-note-list.component.html',
  styleUrls: ['./all-note-list.component.scss']
})
export class AllNoteListComponent implements AfterViewInit, OnDestroy {
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild('filtersForm') filtersForm: NgForm;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @Input() note: Observable<Note[]>;
  @Input() disableLinks = false;
  @Input() partnerObject?: Partner = null;
  @Input() ofwca: ReplaySubject<Ofwca>;
  @Input() ofwcas: Observable<Ofwca[]>;
  @Input() partner: Subject<Partner>;
  @Input() embeddedView = false;
  @Output() changed: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input() excludeColumns: string[] = [];

  initialFilters: any;
  ofwcaObject: Ofwca;
  displayedColumns: string[] = [
    'typeOfMeeting',
    'meetingDateTime',
    'meetingEndDate',
    'partnerOrOfwca',
    'username',
    'actions',
  ];
  notes: Note[] = [];
  totalData = 0;
  typeIds?: number[] = null;
  userPermissions: ReplaySubject<UserPermissions>;
  sortBy = 'meetingDateTime';
  sortOrder = null;
  profile: Profile;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    public dialog: MatDialog,
    private noteService: NoteService,
    private filterService: FilterService,
    private activatedRoute: ActivatedRoute,
    private snack: MatSnackBar,
    private auth: AuthService,
    private store: Store<StoreRootState>,
    private router: Router
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.noteService.getNotesListSubject()
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<Note>) => {
          this.notes = [...e.items];
          this.totalData = e.pagination.total;
        })
      ).subscribe();
    this.parseFilters();
  }
  ngAfterViewInit(): void {
    this.auth.getProfile()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((profile: Profile) => {
        this.profile = profile;
      });
    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe((event: PageEvent) => {
        this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
          page: event.pageIndex,
          pageSize: event.pageSize,
          sortBy: this.sortBy,
          sortOrder: this.sortOrder,
          ...this.filtersForm.value
        })]);
      });
    this.sort.sortChange
      .pipe(takeUntil(this.destroyed$))
      .subscribe((event: Sort) => {
        this.sortChanged(event);
      });

    this.store
      .pipe(
        select(getCurrentRouteState),
        delay(0),
        distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
        takeUntil(this.destroyed$)
      )
      .subscribe((state: RouterStateUrl) => {
        this.parseFilters(state.params);
        this.paginator.pageIndex = state.params.page;
        this.paginator.pageSize = state.params.pageSize;
        this.sortBy = state.params?.sortBy ? state.params.sortBy : 'meetingDateTime';
        this.sortOrder = state.params?.sortOrder ? state.params.sortOrder : 'desc';
        this.loadListData();
      });
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      partner: 'string',
      ofwca: 'string',
      contains$partnerOutsideStructure: 'string',
      gte$meetingDateTime: 'date',
      lte$meetingDateTime: 'date',
      tookPlace: 'int',
      typeOfMeeting: 'int',
      regional: 'string[]',
      username: 'string',
    }, filters);

    if (!filters || Object.keys(filters).length === 0) {
      this.initialFilters.tookPlace = 0;
    }

    this.sortBy = filters?.sortBy || 'meetingDateTime';
    this.sortOrder = filters?.sortOrder ? filters.sortOrder.toUpperCase() : 'DESC';
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  loadListData(): void {
    this.noteService.loadList(
      this.paginator.pageIndex,
      this.paginator.pageSize,
      this.initialFilters,
      {
        sortBy: this.sortBy,
        sortOrder: this.sortOrder
      }
    );
  }

  sortChanged(event: Sort): void {
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path,
      this.filterService.parseFiltersToQueryParams({
        page: this.paginator.pageIndex,
        pageSize: this.paginator.pageSize,
        sortBy: event.active,
        sortOrder: event.direction.toUpperCase(),
        ...this.filtersForm.value
      })
    ]);
  }

  addNote(): void {
    const dialogRef = this.dialog.open(NoteAddDialogComponent, {
      width: '90%',
      data: { partner: this.partnerObject },
      height: '90%',
      maxHeight: '90%',
      panelClass: 'full-height-dialog',
    });

    dialogRef.afterClosed().subscribe(result => {
        this.loadListData();
        this.changed.emit(true);
        this.dialog.closeAll();
    });
  }

  filter(): void {
    const formValues = {...this.filtersForm.value};
    for (const key in formValues) {
      if (formValues[key] === undefined) {
        delete formValues[key];
      }
    }

    if (this.embeddedView) {
      if (this.paginator.pageIndex === 0) {
        this.loadListData();
      } else {
        this.paginator.firstPage();
      }
    } else {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path,
        this.filterService.parseFiltersToQueryParams({
          page: 0,
          pageSize: this.paginator.pageSize,
          sortBy: this.sortBy,
          sortOrder: this.sortOrder,
          ...formValues
        })
      ]);
    }
  }

  buildFilters(): any {
    const filters = { ...this.initialFilters };
    if (this.typeIds !== null) {
      filters.in$type = this.typeIds;
    }
    return filters;
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    this.initialFilters.tookPlace = undefined;

    if (this.embeddedView) {
      this.loadListData();
    } else {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
        page: 0,
        pageSize: this.paginator.pageSize,
      }]);
    }
  }

  downloadReport(): void {
    this.noteService.downloadReport(this.filtersForm.value)
      .subscribe((response: Uuid) => {
          this.dialog.open(JobResultDialogComponent, {
            disableClose: true,
            width: '500px',
            data: { jobId: response.uuid, showProgress: false },
          });
        },
        (error: HttpErrorResponse) => {
          this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), { duration: 2000 });
        }
      );
  }

  deleteNote(note: Note): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: { text: 'Czy na pewno chcesz usunąć notatkę?' },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.noteService.deleteNote(note.uuid)
          .subscribe({
            next: () => {
              this.snack.open('Pomyślnie usunięto notatkę', '', { duration: 2000 });
              this.loadListData();
            },
            error: (response: HttpErrorResponse) => {
              this.snack.open(response.error.error.text, response.status.toString(), { duration: 2000 });
            }
          });
      }
    });
  }
}
